import { buildLogInitialState } from "@/pages/tundish-track/build-log/reducers/build-log.reducer";
import { getError } from "@/utils/get-error";
import { axiosInstance } from "@dexteel/mesf-core";
import { AxiosInstance } from "axios";

type changeRootLocationArgs = {
	rootId: number | null;
	locationId: number | null;
};

export type ReadyForUseAlarmInstance = {
	rootId: number;
	readyForUseSince: string;
};

export type AcknowledgeReadyForUseAlarmsArgs = {
	alarms: ReadyForUseAlarmInstance[];
	operatorWorkerId: number | null;
	comments: string;
};

export type TundishReadyForUseAlarm = {
	rootId: number;
	rootName: string | null;
	readyForUseSince: string;
	tundishReadyForUseAlarmActive: boolean;
	tundishReadyForUsePopupDue: boolean;
	tundishReadyForUseRemainingTime: string;
	tundishReadyForUseOperatorRequired: boolean;
	tundishReadyForUseCommentsEnabled: boolean;
};

export type TundishAlarmConfiguration = {
	tundishAlarmConfigurationId: number;
	alarmCode: string;
	alarmName: string;
	triggerAfterDays: number;
	expiresAfterDays: number;
	repeatPopupAfterHours: number;
	operatorRequired: boolean;
	commentsEnabled: boolean;
	isActive: boolean;
	updateTimestamp: string | null;
	updatedBy: string | null;
};

export class TundishesRepository {
	private getAxiosInstance(): AxiosInstance {
		return axiosInstance;
	}

	async getTundishes() {
		const resp = await this.getAxiosInstance().get("/tundish");
		return resp.data;
	}

	async getTundishAll(isTracked: boolean | null = null) {
		try {
			const resp = await this.getAxiosInstance().get("/tundish/all", {
				params: {
					isTracked,
				},
			});
			return {
				ok: true,
				data: resp.data,
			};
		} catch (e) {
			return {
				ok: false,
				message: getError(e),
			};
		}
	}

	async getParts(componentTypeId: number) {
		const resp = await this.getAxiosInstance().get("/root/parts", {
			params: {
				componentTypeId,
			},
		});
		return {
			error: false,
			data: resp.data,
		};
	}
	async getTundishPlantOverview() {
		const resp = await this.getAxiosInstance().get("/tundish/plant-overview");
		return {
			error: false,
			data: resp.data,
		};
	}

	async acknowledgeReadyForUseAlarms(data: AcknowledgeReadyForUseAlarmsArgs) {
		const resp = await this.getAxiosInstance().patch(
			"/tundish/ready-for-use-alarm/acknowledge",
			data,
		);
		return resp.data;
	}

	async getReadyForUseAlarms() {
		const resp = await this.getAxiosInstance().get<unknown>(
			"/tundish/ready-for-use-alarms",
		);
		const data = resp.data as any;
		const alarms = Array.isArray(data)
			? data
			: Array.isArray(data?.data)
			  ? data.data
			  : Array.isArray(data?.result)
				  ? data.result
				  : [];

		return alarms.map(
			(alarm: any): TundishReadyForUseAlarm => ({
				rootId: alarm.rootId ?? alarm.RootId,
				rootName: alarm.rootName ?? alarm.RootName ?? null,
				readyForUseSince: alarm.readyForUseSince ?? alarm.ReadyForUseSince,
				tundishReadyForUseAlarmActive:
					alarm.tundishReadyForUseAlarmActive ??
					alarm.TundishReadyForUseAlarmActive ??
					false,
				tundishReadyForUsePopupDue:
					alarm.tundishReadyForUsePopupDue ??
					alarm.TundishReadyForUsePopupDue ??
					false,
				tundishReadyForUseRemainingTime:
					alarm.tundishReadyForUseRemainingTime ??
					alarm.TundishReadyForUseRemainingTime ??
					"",
				tundishReadyForUseOperatorRequired:
					alarm.tundishReadyForUseOperatorRequired ??
					alarm.TundishReadyForUseOperatorRequired ??
					true,
				tundishReadyForUseCommentsEnabled:
					alarm.tundishReadyForUseCommentsEnabled ??
					alarm.TundishReadyForUseCommentsEnabled ??
					true,
			}),
		);
	}

	async getAlarmConfiguration() {
		const resp = await this.getAxiosInstance().get<TundishAlarmConfiguration>(
			"/tundish/alarm-configuration",
		);
		return resp.data;
	}

	async updateAlarmConfiguration(data: Partial<TundishAlarmConfiguration>) {
		const resp = await this.getAxiosInstance().patch<TundishAlarmConfiguration>(
			"/tundish/alarm-configuration",
			data,
		);
		return resp.data;
	}

	async getRootDetails(rootId: number) {
		const resp = await this.getAxiosInstance().get("/tundish/details", {
			params: {
				rootId,
			},
		});
		return {
			error: false,
			data: resp.data,
		};
	}

	async changeRootLocation({
		rootId = null,
		locationId,
	}: changeRootLocationArgs) {
		const resp = await this.getAxiosInstance().patch(
			`/tundish/location`,
			{},
			{
				params: {
					rootId,
					locationId,
				},
			},
		);
		return resp.data;
	}

	async setRootState(data: setRootStateArgs) {
		const resp = await this.getAxiosInstance().patch(
			`/Root/status`,
			{},
			{
				params: {
					...data,
				},
			},
		);
		if (resp.data.success) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return Promise.reject(resp.data);
		}
	}

	async getComponentsListForTundishId(tundishId: number) {
		const resp = await this.getAxiosInstance().get(
			`/tundish/${tundishId}/components`,
		);
		if (resp.status == 200) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return {
				ok: false,
				data: resp.data,
			};
		}
	}

	async getSpecialComponentsListForTundishId(tundishId: number) {
		const resp = await this.getAxiosInstance().get(`/special-components`, {
			params: {
				tundishId,
			},
		});
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getCampaignsForComponent(tundishId: number, componentTypeId: number) {
		const resp = await this.getAxiosInstance().get(
			`/tundish/${tundishId}/components/${componentTypeId}/campaigns`,
		);
		if (resp.status === 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getTundishTasks(
		tundishId: number,
		params: { [key: string]: any } = {},
	) {
		const resp = await this.getAxiosInstance().get(
			`/Tundish/${tundishId}/tasks`,
			{
				params: {
					...params,
				},
			},
		);
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getTundishHeats(
		tundishId: number,
		params: { [key: string]: any } = {},
	) {
		const resp = await this.getAxiosInstance().get(
			`/Tundish/${tundishId}/heats`,
			{
				params: {
					...params,
				},
			},
		);
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getCampaignList() {
		const resp = await this.getAxiosInstance().get(`/Tundish/campaigns`);
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getTundishBuildLog(filters: (typeof buildLogInitialState)["filters"]) {
		const { startDateTime, endDateTime, interval, sequenceName, tundishes } =
			filters;

		const resp = await this.getAxiosInstance().get(`/sequence/all-by-tundish`, {
			params: {
				interval,
				start: startDateTime,
				end: endDateTime,
				sequenceName: sequenceName,
				tundishesCSV: tundishes?.join(",") || "All",
			},
		});
		if (resp.status === 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getTundishTrackReport(filters: { start: string; end: string }) {
		const resp = await this.getAxiosInstance().post(`/Backend/Execute`, {
			method: "NSB.TundishReport",
			parameters: [
				{
					name: "Start",
					value: filters.start,
				},
				{
					name: "End",
					value: filters.end,
				},
			],
		});
		if (resp.status === 200) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return {
				ok: false,
				data: resp.data,
			};
		}
	}

	async getHeatEventsForTundish(
		tundishId: number,
		params: { [key: string]: any } = {},
	) {
		const resp = await this.getAxiosInstance().get(`/heat/events`, {
			params: {
				rootId: tundishId,
				start: params.from || null,
				end: params.to || null,
			},
		});
		if (resp.status !== 200) {
			return {
				error: false,
				data: resp.data,
			};
		} else {
			return {
				error: true,
				data: resp.data,
			};
		}
	}

	async getRootEvents(params: any) {
		const resp = await this.getAxiosInstance().get(`/root/events`, {
			params: {
				...params,
			},
		});
		if (resp.status === 200) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return {
				ok: false,
				data: resp.data,
			};
		}
	}
	async getTundishById(tundishId: number) {
		const resp = await this.getAxiosInstance().get(`/tundish/${tundishId}`);
		if (resp.status === 200) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return {
				ok: false,
				data: resp.data,
			};
		}
	}
	async changeRootStatus(data: {
		rootId: number;
		locationId: number | undefined;
		rootStateId: number | undefined;
		supervisorInstructions: string;
		stateComments: string;
		operatorComments: string;
		sequenceName: string;
	}) {
		try {
			const resp = await this.getAxiosInstance().patch(
				`/tundish/status`,
				{},
				{
					params: {
						...data,
					},
				},
			);
			if (resp.data.success) {
				return {
					ok: true,
					data: resp.data.result,
				};
			}
			return resp.data;
		} catch (e) {
			return {
				ok: false,
				data: getError(e),
			};
		}
	}
	async putTundishInOOS(tundishId: number) {
		const resp = await this.getAxiosInstance().post(
			`/special-components/${tundishId}/oos`,
		);
		if (resp.status === 200) {
			return {
				ok: true,
				data: resp.data,
			};
		} else {
			return {
				ok: false,
				data: resp.data,
			};
		}
	}
}

export interface setRootStateArgs {
	heatName: string;
	needsAttention: boolean;
	attentionComments: string;
	rootStateId: number;
	rootId: number;
	workerComments: string;
	stateComments: string;
	supervisorInstructions: string;
	locationId?: number;
}
export const tundishesRepository = new TundishesRepository();
